import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { RecommendedQuestion } from '../../../../services/recommendedQuestion';
import ConfirmModal from '../../../../components/common/ConfirmModal';

interface EnabledQuestionItemProps {
  question: RecommendedQuestion;
  slot: number;
  onDisable: (id: string) => void;
  onDelete: (id: string) => void;
}

export default function EnabledQuestionItem({ question, slot, onDisable, onDelete }: EnabledQuestionItemProps) {
  const { t } = useTranslation();
  const [confirmDisable, setConfirmDisable] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: question.ID });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  };

  return (
    <>
      <div
        ref={setNodeRef}
        style={style}
        className={`flex items-center gap-4 px-5 py-4 border-b border-gray-100 last:border-b-0 transition-colors group ${isDragging ? 'bg-rose-50/30' : 'bg-white hover:bg-gray-50/40'}`}
      >
        {/* 拖拽手柄 */}
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="w-6 h-6 flex items-center justify-center text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing flex-shrink-0 transition-colors"
          title={t('rec.dragHandle')}
        >
          <i className="ri-drag-move-2-fill text-base"></i>
        </button>

        {/* 序号 */}
        <div
          className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 text-white select-none"
          style={{ backgroundColor: '#830051' }}
        >
          {slot}
        </div>

        {/* 问题内容 */}
        <span className="flex-1 text-sm text-gray-800 leading-relaxed">{question.Question}</span>

        {/* 操作按钮 */}
        <div className="flex items-center gap-1.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            type="button"
            onClick={() => setConfirmDisable(true)}
            className="px-2.5 py-1.5 rounded-md text-xs font-medium bg-gray-100 text-gray-600 hover:bg-red-50 hover:text-red-600 cursor-pointer whitespace-nowrap transition-colors"
          >
            {t('rec.disable')}
          </button>
          <Link
            to={`/admin/recommended-questions/${question.ID}`}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 cursor-pointer transition-colors"
            title={t('common.edit')}
          >
            <i className="ri-edit-2-line text-sm"></i>
          </Link>
          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 cursor-pointer transition-colors"
            title={t('common.delete')}
          >
            <i className="ri-delete-bin-line text-sm"></i>
          </button>
        </div>
      </div>

      {/* 禁用确认弹窗 */}
      <ConfirmModal
        open={confirmDisable}
        title={t('rec.disableConfirm')}
        description={question.Question}
        confirmText={t('rec.disable')}
        onConfirm={() => { onDisable(question.ID); setConfirmDisable(false); }}
        onCancel={() => setConfirmDisable(false)}
      />

      {/* 删除确认弹窗 */}
      <ConfirmModal
        open={confirmDelete}
        title={t('rec.deleteConfirm')}
        description={question.Question}
        confirmText={t('common.delete')}
        confirmColor="danger"
        onConfirm={() => { onDelete(question.ID); setConfirmDelete(false); }}
        onCancel={() => setConfirmDelete(false)}
      />
    </>
  );
}
